import React from "react";
import { useTranslation } from "react-i18next";
import { Typography, Card, Row, Col } from "antd";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import styled from "styled-components";
import { KeyOutlined } from "@ant-design/icons";

import { WHITE, GRAY_500, PRIMARY_600, GRAY_200 } from "../constant/colors";
import { NotificationSettingsIcon } from "../assets/svg/NotificationSettingsIcon";
import PhysicianManagementIcon from "../assets/svg/PhysicianManagementIcon";
import { AutomaticRulesIcon } from "../assets/svg/AutomaticRulesIcon";
import { SettingsIcon } from "../assets/svg/SettingsIcon";
import { selectUser, selectEnvironment } from "../store/selector";
import { usePortalCredentialsEnabled } from "../hooks/usePortalCredentialsEnabled";
import { useStandardizedTracking, EventNames, ContentTypes } from "../utils/pendo/useStandardizedTracking";
import { useGetSetting } from "../hooks/getSetting";

const { Text } = Typography;

const SettingsScreen = styled.div`
  width: 98%;
  margin: 0 auto;
  padding: 12px;
  background: ${WHITE};
  border-radius: 12px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  height: calc(100vh - 100px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const SectionContainer = styled.div`
  padding: 26px 10px;
  border-bottom: 1px solid ${GRAY_200};

  &:first-child {
    padding-top: 0px;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
`;

const SectionTitle = styled.div`
  font-family: "Open Sans";
  font-size: 16px;
  font-weight: 600;
  color: #000000;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SectionDescription = styled(Text)`
  font-size: 14px;
  color: ${GRAY_500};
  display: block;
  margin-bottom: 16px;
`;

const SettingsCard = styled(Card)`
  border-radius: 10px;
  border: 1px solid ${GRAY_500};
  cursor: pointer;
  transition: all 0.2s;
  width: 346px;
  height: 108px;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
    transform: translateY(-2px);
  }

  .ant-card-body {
    padding: 24px;
    height: 100%;
    display: flex;
    align-items: center;
  }
`;

const CardIcon = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  margin-right: 16px;
  width: 39.81px;
  height: 39.43px;
`;

const SettingsCardIcon = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  margin-right: 16px;
  width: 40px;
  height: 40px;
  background: ${PRIMARY_600}15;
  color: ${PRIMARY_600};
  font-size: 20px;
`;

const CardsContainer = styled.div`
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
`;

const SettingsComponent = () => {
  const { t } = useTranslation();
  const user = useSelector(selectUser);
  const account = useSelector(selectEnvironment);
  const [isPendingActionsEnabled] = useGetSetting(["pending-actions.enabled"]);
  const { track } = useStandardizedTracking();

  const isPortalCredentialsEnabled = usePortalCredentialsEnabled();

  const handleNotificationSettingsClick = () => {
    track(EventNames.BUTTON_CLICK, {
      featureGroup: "settings",
      pageKey: "settings",
      contentKey: "notification_preferences",
      contentType: ContentTypes.BUTTON,
      additionalInfo: {
        username: user?.email,
        account
      }
    });
  };

  const handleAutomaticRulesClick = () => {
    track(EventNames.BUTTON_CLICK, {
      featureGroup: "settings",
      pageKey: "settings",
      contentKey: "automatic_rules",
      contentType: ContentTypes.BUTTON,
      additionalInfo: {
        username: user?.email,
        account
      }
    });
  };

  const handlePhysiciansClick = () => {
    track(EventNames.BUTTON_CLICK, {
      featureGroup: "settings",
      pageKey: "settings",
      contentKey: "physicians",
      contentType: ContentTypes.BUTTON,
      additionalInfo: {
        username: user?.email,
        account
      }
    });
  };

  return (
    <SettingsScreen>
      <SectionContainer>
        <SectionHeader>
          <SettingsIcon width="20px" height="20px" />
          <SectionTitle>{t("settings.my_preferences_title")}</SectionTitle>
        </SectionHeader>
        <SectionDescription>{t("settings.my_preferences_subtitle")}</SectionDescription>

        <CardsContainer>
          <Link
            to="/notification-preferences"
            style={{ textDecoration: "none", display: "inline-block" }}
            onClick={handleNotificationSettingsClick}
          >
            <SettingsCard id="Settings_Notification_Preferences">
              <Row align="middle">
                <Col>
                  <CardIcon>
                    <NotificationSettingsIcon width="39.81px" height="39.43px" />
                  </CardIcon>
                </Col>
                <Col flex="1">
                  <Typography.Title level={4} style={{ margin: 0, marginBottom: 1 }}>
                    {t("settings.notification_settings_title")}
                  </Typography.Title>
                  <Typography.Text type="secondary">{t("settings.notification_settings_description")}</Typography.Text>
                </Col>
              </Row>
            </SettingsCard>
          </Link>

          {isPortalCredentialsEnabled && (
            <Link
              to="/portal-credentials"
              style={{ textDecoration: "none", display: "inline-block" }}
              data-testid="portal-credentials-settings-card"
            >
              <SettingsCard id="Settings_Portal_Credentials">
                <Row align="middle">
                  <Col>
                    <SettingsCardIcon>
                      <KeyOutlined />
                    </SettingsCardIcon>
                  </Col>
                  <Col flex="1">
                    <Typography.Title level={4} style={{ margin: 0, marginBottom: 1 }}>
                      {t("settings.portal_credentials_title")}
                    </Typography.Title>
                    <Typography.Text type="secondary">{t("settings.portal_credentials_description")}</Typography.Text>
                  </Col>
                </Row>
              </SettingsCard>
            </Link>
          )}
        </CardsContainer>
      </SectionContainer>

      <SectionContainer>
        <SectionHeader>
          <SettingsIcon width="20px" height="20px" />
          <SectionTitle>{t("settings.shared_site_settings_title")}</SectionTitle>
        </SectionHeader>
        <SectionDescription>{t("settings.shared_site_settings_subtitle")}</SectionDescription>

        <Row gutter={16}>
          <Col>
            <Link
              to="/physicians-settings"
              style={{ textDecoration: "none", display: "inline-block" }}
              onClick={handlePhysiciansClick}
            >
              <SettingsCard id="Settings_Physicians">
                <Row align="middle">
                  <Col>
                    <CardIcon>
                      <PhysicianManagementIcon />
                    </CardIcon>
                  </Col>
                  <Col flex="1">
                    <Typography.Title level={4} style={{ margin: 0, marginBottom: 1 }}>
                      {t("settings.physicians_title")}
                    </Typography.Title>
                    <Typography.Text type="secondary">{t("settings.physicians_description")}</Typography.Text>
                  </Col>
                </Row>
              </SettingsCard>
            </Link>
          </Col>
          {isPendingActionsEnabled && (
            <Col>
              <Link
                to="/automatic-rules"
                style={{ textDecoration: "none", display: "inline-block" }}
                onClick={handleAutomaticRulesClick}
              >
                <SettingsCard id="Settings_Automatic_Rules">
                  <Row align="middle">
                    <Col>
                      <CardIcon>
                        <AutomaticRulesIcon width="39.81px" height="39.43px" />
                      </CardIcon>
                    </Col>
                    <Col flex="1">
                      <Typography.Title level={4} style={{ margin: 0, marginBottom: 1 }}>
                        {t("settings.automatic_rules_title")}
                      </Typography.Title>
                      <Typography.Text type="secondary">{t("settings.automatic_rules_description")}</Typography.Text>
                    </Col>
                  </Row>
                </SettingsCard>
              </Link>
            </Col>
          )}
        </Row>
      </SectionContainer>
    </SettingsScreen>
  );
};

export { SettingsComponent as Settings };
